import { useEffect, useState } from 'react';
import {
  usePartnerDispatch,
  usePartnerSelector,
  selectProducts,
  selectActiveProductTag
} from '../../store';
import { switchProduct } from '../../store/multiProductSlice';
import { fetchShopifyProductInfo, ShopifyProductInfo } from '../../utils/api';
import { ParsedProductConfig } from '../../types';

interface SelectProductTabProps {
  onProductSelect: () => void;
}

function stripHtml(html?: string | null): string {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent?.trim() ?? '';
}

function truncateText(text: string, maxLength = 256): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trimEnd()}...`;
}

function formatPriceRange(
  priceMin?: string,
  priceMax?: string,
  currency?: string
): string {
  const cur = currency || 'USD';
  if (!priceMin && !priceMax) return '';
  const min = priceMin ? parseFloat(priceMin).toFixed(2) : null;
  const max = priceMax ? parseFloat(priceMax).toFixed(2) : null;
  if (min && max && min !== max) return `$${min} – $${max} ${cur}`;
  return `$${min ?? max} ${cur}`;
}

/**
 * Sort products: default (isDefault === true or index 0) first,
 * then remaining products alphabetically by title.
 */
function sortProducts(products: ParsedProductConfig[]): ParsedProductConfig[] {
  if (products.length === 0) return [];
  const defaultProduct = products.find(p => p.isDefault) ?? products[0];
  const rest = products
    .filter(p => p !== defaultProduct)
    .sort((a, b) => (a.productTitle ?? '').localeCompare(b.productTitle ?? ''));
  return [defaultProduct, ...rest];
}

/**
 * SelectProductTab
 *
 * Renders a dropdown of available products. Selecting a new product switches the
 * active product immediately and navigates back to the Customize tab.
 */
export function SelectProductTab({ onProductSelect }: SelectProductTabProps) {
  const dispatch = usePartnerDispatch();
  const products = usePartnerSelector(selectProducts);
  const activeProductTag = usePartnerSelector(selectActiveProductTag);

  const sorted = sortProducts(products);

  const [shopifyInfo, setShopifyInfo] = useState<
    Record<string, ShopifyProductInfo>
  >({});
  const [hiddenImageTags, setHiddenImageTags] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    const tags = products.map(p => p.productTag).filter(Boolean);
    if (tags.length === 0) return;
    fetchShopifyProductInfo(tags)
      .then(info => setShopifyInfo(info))
      .catch(() => {
        /* non-fatal */
      });
  }, [products]);

  const previewProduct =
    sorted.find(p => p.productTag === activeProductTag) ?? sorted[0];
  const info = previewProduct ? shopifyInfo[previewProduct.productTag] : null;
  const thumbnailUrl =
    previewProduct && !hiddenImageTags[previewProduct.productTag]
      ? info?.thumbnail_url ?? null
      : null;
  const description = truncateText(stripHtml(info?.description), 256);
  const priceRange = previewProduct
    ? formatPriceRange(
        previewProduct.productPriceMin,
        previewProduct.productPriceMax,
        previewProduct.productCurrency
      )
    : '';

  function handleSelectProduct(productTag: string) {
    if (productTag !== activeProductTag) {
      dispatch(switchProduct(productTag));
    }
    onProductSelect();
  }

  return (
    <div style={{ padding: '16px' }}>
      {/* Product dropdown */}
      <label
        htmlFor="select-product-dropdown"
        style={{
          display: 'block',
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--nb-text, #424242)',
          marginBottom: '6px'
        }}
      >
        Select a Product
      </label>
      <select
        id="select-product-dropdown"
        value={previewProduct?.productTag ?? ''}
        onChange={e => handleSelectProduct(e.target.value)}
        style={{
          width: '100%',
          padding: '10px 12px',
          fontSize: '14px',
          border: '1px solid var(--nb-border, #e5e7eb)',
          borderRadius: '8px',
          backgroundColor: 'var(--nb-body-bg, #ffffff)',
          color: 'var(--nb-text, #424242)',
          cursor: 'pointer',
          outline: 'none'
        }}
      >
        {sorted.map(product => (
          <option key={product.productTag} value={product.productTag}>
            {product.productTitle ?? product.productTag}
            {product.productTag === activeProductTag ? ' (active)' : ''}
          </option>
        ))}
      </select>

      {/* Detail panel */}
      {previewProduct && (
        <div
          style={{
            marginTop: '16px',
            border: '1px solid var(--nb-border, #e5e7eb)',
            borderRadius: '8px',
            backgroundColor: 'var(--nb-body-bg, #ffffff)',
            padding: '14px 16px'
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '14px',
              alignItems: 'flex-start'
            }}
          >
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={previewProduct.productTitle ?? 'Product'}
                onError={() =>
                  setHiddenImageTags(prev => ({
                    ...prev,
                    [previewProduct.productTag]: true
                  }))
                }
                style={{
                  width: '88px',
                  height: '88px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  flexShrink: 0
                }}
              />
            ) : null}

            <div style={{ minWidth: 0, flex: 1 }}>
              <p
                style={{
                  fontSize: '15px',
                  fontWeight: 700,
                  color: 'var(--nb-text, #424242)',
                  marginBottom: priceRange ? '6px' : 0
                }}
              >
                {previewProduct.productTitle ?? previewProduct.productTag}
              </p>

              {priceRange && (
                <p
                  style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: 'var(--nb-text, #374151)',
                    marginBottom: description ? '8px' : 0
                  }}
                >
                  {priceRange}
                </p>
              )}

              {description && (
                <p
                  style={{
                    fontSize: '12px',
                    color: 'var(--nb-text-secondary, #6b7280)',
                    lineHeight: '1.5',
                    margin: 0
                  }}
                >
                  {description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
